/**
 * index.js — вход: кабинет динамических кодов + проверка печати.
 *
 * Два куска, объединённые одной страницей и одним мостом к рисовальщику
 * (`window.__qrTool`). Кабинет — про то, куда код ведёт; проверка — про то,
 * прочитается ли он вообще.
 *
 * ⚠️ ПРОВЕРКА РАБОТАЕТ ДЛЯ ЛЮБОГО КОДА, а не только для динамического. Ломает
 * читаемость не природа кода, а оформление: логотип на треть площади, светло-
 * серый на белом, «капельки» вместо квадратов, уровень коррекции L при длинной
 * ссылке. Это одинаково губит и статический код на визитке, и динамический на
 * витрине, и узнают об этом одинаково — из типографии.
 */

import { mount } from './panel.js';
import { probe, verdict, distanceCm, widthCm } from './preflight.js';

const CFG = window.__QRD_CFG || {};
const T = CFG.i18n || {};

const $ = (id) => document.getElementById(id);

function t(key, vars) {
    let s = T[key] || key;
    if (vars) {
        for (const k in vars) {
            s = s.split(':' + k).join(String(vars[k]));
        }
    }
    return s;
}

/* ─────────────────────────── проверка печати ────────────────────────────── */

let lastSig = null;
let timer = null;
let running = false;

const ui = {};

/**
 * Подпись состояния: пока она не изменилась, перепроверять нечего.
 *
 * В подпись входит ВСЁ, что попадает в пиксели: строка, цвета, стиль точек,
 * логотип, уровень коррекции, рамка. Забыть здесь одно поле — значит показывать
 * вердикт от прошлого оформления, и это худший из возможных результатов:
 * зелёная галочка на коде, который сломали минуту назад.
 */
function signature(snap) {
    return [
        snap.text, snap.modules, snap.ecLevel,
        snap.fgColor, snap.bgColor, snap.bgTransparent ? 1 : 0,
        snap.dotStyle, snap.cornerSquare, snap.cornerDot,
        snap.gradient, snap.gradientColor2, snap.gradientAngle,
        snap.logo ? 'L' + snap.logoSize : '-',
        snap.frameType,
    ].join('|');
}

function schedule() {
    clearTimeout(timer);
    timer = setTimeout(run, 700);
}

async function run() {
    if (!window.__qrTool || running) return;

    const snap = window.__qrTool.snapshot();
    if (!snap || !snap.text || !snap.modules) {
        setState('idle');
        return;
    }

    const sig = signature(snap);
    if (sig === lastSig) return;

    running = true;
    setState('busy');

    try {
        // ⚠️ Проверяем ЭКСПОРТНЫЙ холст, а не превью. Превью 400 px, а человек
        // скачает 1024 — на разных размерах логотип занимает разную ДОЛЮ, и
        // вердикт по превью отвечал бы про картинку, которой никто не получит.
        const canvas = window.__qrTool.exportCanvas();
        // Эталон — та же матрица без оформления. Без него прибор мерил бы
        // плотность кода (то есть длину ссылки), а не цену украшений.
        const plain = window.__qrTool.plainCanvas ? window.__qrTool.plainCanvas() : null;
        const res = await probe(canvas, plain, snap.modules, snap.text);

        lastSig = sig;
        paint(res, snap);
    } catch (e) {
        setState('idle');
    } finally {
        running = false;
    }
}

function setState(s) {
    if (ui.box) ui.box.setAttribute('data-state', s);
}

function paint(res, snap) {
    const v = verdict(res);

    if (v === 'unknown') {
        setState('idle');
        return;
    }

    setState(v);

    if (ui.verdict) {
        ui.verdict.textContent = t('pf_' + v);
    }

    if (ui.detail) {
        if (v === 'broken') {
            // Самая ценная строка на странице: код НЕ читается вообще, и без
            // нас человек узнал бы это из типографии.
            ui.detail.textContent = t('pf_broken_hint');
        } else if (v === 'heavy' && res.penalty) {
            // Говорим не «плохо», а НА СКОЛЬКО плохо и в чём это выражается:
            // «печатать крупнее во столько-то раз» — единственная форма, из
            // которой следует действие.
            ui.detail.textContent = t('pf_penalty', {
                times: String(Math.round(res.penalty * 10) / 10).replace('.', ','),
            });
        } else if (res.ppm) {
            ui.detail.textContent = t('pf_measured', { modules: snap.modules });
        } else {
            ui.detail.textContent = t('pf_heavy_hint');
        }
    }

    // Советы — только те, что следуют из ЭТОГО состояния. Общий список
    // «повысьте контраст, уменьшите логотип» под каждым кодом читается как шум
    // и не читается вовсе.
    if (ui.tips) {
        ui.tips.textContent = '';
        const tips = [];

        if (snap.logo && snap.logoSize >= 18) {
            tips.push(t('tip_logo'));
        }
        if (snap.ecLevel === 'L' || snap.ecLevel === 'M') {
            tips.push(t('tip_ec'));
        }
        if (contrast(snap.fgColor, snap.bgColor) < 4) {
            tips.push(t('tip_contrast'));
        }
        if (snap.dotStyle && snap.dotStyle !== 'square') {
            tips.push(t('tip_dots'));
        }

        if (v !== 'clean' && tips.length) {
            tips.slice(0, 3).forEach((x) => {
                const li = document.createElement('li');
                li.textContent = x;
                ui.tips.appendChild(li);
            });
            ui.tips.hidden = false;
        } else {
            ui.tips.hidden = true;
        }
    }

    syncSize(res.ppm || 2);
}

/** Контраст по WCAG — та же формула, что в color-accessibility-checker. */
function contrast(a, b) {
    const lum = (hex) => {
        const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(String(hex || ''));
        if (!m) return 1;
        const ch = [1, 2, 3].map((i) => {
            const c = parseInt(m[i], 16) / 255;
            return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
        });
        return 0.2126 * ch[0] + 0.7152 * ch[1] + 0.0722 * ch[2];
    };
    const l1 = lum(a);
    const l2 = lum(b);
    return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
}

/* ────────────────────── советник по размеру печати ──────────────────────── */

let lastPpm = 2;

/**
 * Две стороны одного вопроса, и обе нужны.
 *
 * «Печатаю 3 см — с какого расстояния возьмут?» спрашивает тот, у кого макет
 * уже свёрстан. «Сканируют с двух метров — какой ширины печатать?» спрашивает
 * тот, кто делает баннер. Оставить одну сторону значит заставить половину людей
 * считать в уме обратную задачу.
 */
function syncSize(ppm) {
    lastPpm = ppm || 2;

    if (ui.width && ui.widthOut) {
        const w = Number(ui.width.value) || 3;
        const d = distanceCm(w, lastPpm);
        ui.widthOut.textContent = d ? t('size_reach', { d }) : '';
    }

    if (ui.dist && ui.distOut) {
        const d = Number(ui.dist.value) || 30;
        const w = widthCm(d, lastPpm);
        ui.distOut.textContent = w ? t('size_need', { w: String(w).replace('.', ',') }) : '';
    }
}

/* ───────────────────────────────── старт ────────────────────────────────── */

function boot() {
    mount(CFG);

    ui.box = $('qrPreflight');
    ui.verdict = $('qrPfVerdict');
    ui.detail = $('qrPfDetail');
    ui.tips = $('qrPfTips');
    ui.width = $('qrPfWidth');
    ui.widthOut = $('qrPfWidthOut');
    ui.dist = $('qrPfDist');
    ui.distOut = $('qrPfDistOut');

    if (!ui.box) return;

    ui.width && ui.width.addEventListener('input', () => syncSize(lastPpm));
    ui.dist && ui.dist.addEventListener('input', () => syncSize(lastPpm));

    // Генератор сообщает о каждой перерисовке — своего опроса по таймеру не
    // заводим: он тикал бы и на неподвижной странице (см. запрет на фоновые
    // петли в CLAUDE.md).
    document.addEventListener('qr-tool:rendered', schedule);

    syncSize(2);
    schedule();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
} else {
    boot();
}
